import { Injectable } from '@angular/core';
import { LlmService, AutosuggestRequest } from './llm.service';

export interface WorkflowReviewItem {
  actionIndex: number;
  actionKey: string;
  fieldKey: string;
  fieldLabel: string;
  currentText: string;
  suggestion?: string;
  type: 'suggestion' | 'issue';
}

export interface WorkflowReview {
  items: WorkflowReviewItem[];
  hasIssues: boolean;
  hasSuggestions: boolean;
  timestamp: string;
}

/**
 * Service for reviewing entire workflows with AI
 * Checks all actions and provides suggestions before publishing
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowAiReviewService {
  constructor(private llmService: LlmService) {}

  /**
   * Review all actions in a workflow
   * @param workflowData The workflow form data with trigger and actions
   * @returns Promise with review items and suggestions
   */
  async reviewWorkflow(workflowData: {
    name: string;
    trigger: any;
    actions: any[];
  }): Promise<WorkflowReview> {
    // Build a summary of the workflow to review in one call
    const workflowSummary = this.buildWorkflowSummary(workflowData);

    if (!workflowSummary) {
      return {
        items: [],
        hasIssues: false,
        hasSuggestions: false,
        timestamp: new Date().toISOString()
      };
    }

    try {
      const systemPrompt = `You are reviewing workflows in TIDY (property management software). Focus on clarity and actionability.

Format your response as a brief list organized by action:
- One specific improvement per action (if needed)
- Start each line with the action number/name
- Be concise - no separate sections or verbose explanations
- Use simple markdown (bold, line breaks)

"Concierge Actions" = tasks for human staff, so require clear instructions.

If workflow is good as-is, respond with hasSuggestion: false.
Return JSON: { "hasSuggestion": boolean, "suggestion": "your brief feedback or empty string" }`;

      const responseSchema = {
        type: 'object',
        properties: {
          hasSuggestion: { type: 'boolean' },
          suggestion: { type: 'string' }
        },
        required: ['hasSuggestion', 'suggestion']
      };

      const request: AutosuggestRequest = {
        systemPrompt,
        prompt: workflowSummary,
        responseSchema,
        maxTokens: 300
      };

      const response = await this.llmService.suggest(request);

      const items: WorkflowReviewItem[] = [];

      if (response.result?.hasSuggestion && response.result?.suggestion) {
        // Create a single review item for the entire workflow
        items.push({
          actionIndex: 0,
          actionKey: 'workflow',
          fieldKey: 'overall',
          fieldLabel: 'Workflow Review',
          currentText: workflowSummary,
          suggestion: response.result.suggestion,
          type: 'suggestion'
        });
      }

      return {
        items,
        hasIssues: false,
        hasSuggestions: items.length > 0,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error reviewing workflow:', error);
      return {
        items: [],
        hasIssues: false,
        hasSuggestions: false,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Build a concise summary of the workflow for review
   */
  private buildWorkflowSummary(workflowData: {
    name: string;
    trigger: any;
    actions: any[];
  }): string {
    // Don't review if no actions
    if (!workflowData.actions || workflowData.actions.length === 0) {
      return '';
    }

    const parts: string[] = [];

    parts.push(`Workflow: ${workflowData.name}`);

    if (workflowData.trigger) {
      parts.push(`Trigger: ${workflowData.trigger.name || 'Unknown'}`);
    }

    parts.push(`\nActions:`);

    let hasContent = false;
    workflowData.actions.forEach((action, index) => {
      if (!action || !action.key) {return;}

      parts.push(`\n${index + 1}. ${action.name || action.key}`);
      hasContent = true;

      const inputValues = action.input_values || {};
      for (const [fieldKey, fieldValue] of Object.entries(inputValues)) {
        if (typeof fieldValue === 'string' && fieldValue.trim()) {
          parts.push(`   ${this.formatFieldLabel(fieldKey)}: ${fieldValue}`);
        }
      }
    });

    return hasContent ? parts.join('\n') : '';
  }

  /**
   * Convert field key to display label (e.g., 'subject' -> 'Subject', 'guest_note' -> 'Guest Note')
   */
  private formatFieldLabel(fieldKey: string): string {
    return fieldKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

}
